// Batch frame extraction modal - extract frames at intervals or specific points
import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Clock, Film, Camera, Loader2, AlertCircle } from 'lucide-react'
import { formatFileSize } from '@/lib/utils'

export type ExtractionMode = 'interval' | 'count' | 'range'

interface BatchExtractionConfig {
  mode: ExtractionMode
  // Interval mode: extract every N seconds
  intervalSeconds: number
  // Count mode: extract N frames evenly distributed
  frameCount: number
  // Range mode: extract frames between start/end time
  rangeStart: number
  rangeEnd: number
  rangeIntervalSeconds: number
}

interface BatchExtractionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  videoDuration: number  // in seconds
  videoFps: number
  videoName: string
  totalFrames: number
  onExtract: (frames: number[]) => Promise<void>
}

export function BatchExtractionModal({
  open,
  onOpenChange,
  videoDuration,
  videoFps,
  videoName,
  totalFrames,
  onExtract,
}: BatchExtractionModalProps) {
  const [config, setConfig] = useState<BatchExtractionConfig>({
    mode: 'interval',
    intervalSeconds: 5,
    frameCount: 10,
    rangeStart: 0,
    rangeEnd: videoDuration,
    rangeIntervalSeconds: 2,
  })
  const [isExtracting, setIsExtracting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Calculate frames to extract based on config
  const framesToExtract = useMemo(() => {
    const frames: number[] = []
    
    switch (config.mode) {
      case 'interval': {
        // Extract every N seconds
        for (let time = 0; time <= videoDuration; time += config.intervalSeconds) {
          const frame = Math.floor(time * videoFps) + 1 // 1-based frame number
          if (frame <= totalFrames) {
            frames.push(frame)
          }
        }
        break
      }
      case 'count': {
        // Extract N frames evenly distributed
        if (config.frameCount > 0 && config.frameCount <= totalFrames) {
          const step = totalFrames / config.frameCount
          for (let i = 0; i < config.frameCount; i++) {
            const frame = Math.floor(i * step) + 1
            if (frame <= totalFrames && !frames.includes(frame)) {
              frames.push(frame)
            }
          }
        }
        break
      }
      case 'range': {
        // Extract frames within time range at interval
        const startFrame = Math.floor(config.rangeStart * videoFps) + 1
        const endFrame = Math.min(Math.floor(config.rangeEnd * videoFps) + 1, totalFrames)
        for (let time = config.rangeStart; time <= config.rangeEnd; time += config.rangeIntervalSeconds) {
          const frame = Math.floor(time * videoFps) + 1
          if (frame >= startFrame && frame <= endFrame && !frames.includes(frame)) {
            frames.push(frame)
          }
        }
        break
      }
    }
    
    return frames.sort((a, b) => a - b)
  }, [config, videoDuration, videoFps, totalFrames])

  // Estimate file size (rough estimate: 200KB per frame)
  const estimatedSize = framesToExtract.length * 200 * 1024

  const handleExtract = async () => {
    if (framesToExtract.length === 0) return
    
    setIsExtracting(true)
    setProgress(0)
    setError(null)
    
    try {
      // Simulate progress updates for UX
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 95))
      }, 200)
      
      await onExtract(framesToExtract)
      
      clearInterval(progressInterval)
      setProgress(100)
      
      // Close after brief delay to show completion
      setTimeout(() => {
        onOpenChange(false)
        setProgress(0)
        setIsExtracting(false)
      }, 500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed')
      setIsExtracting(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Batch Frame Extraction
          </DialogTitle>
          <DialogDescription>
            Extract multiple frames from <span className="font-medium">{videoName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Video info */}
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
            <Film className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm">
              <div className="font-medium">{videoName}</div>
              <div className="text-muted-foreground">
                {formatTime(videoDuration)} • {totalFrames.toLocaleString()} frames • {videoFps} fps
              </div>
            </div>
          </div>

          {/* Extraction mode */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Extraction Mode</Label>
            <RadioGroup
              value={config.mode}
              onValueChange={(value) => setConfig({ ...config, mode: value as ExtractionMode })}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="interval" id="interval" />
                <Label htmlFor="interval" className="cursor-pointer">
                  Every N seconds
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="count" id="count" />
                <Label htmlFor="count" className="cursor-pointer">
                  Specific number of frames
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="range" id="range" />
                <Label htmlFor="range" className="cursor-pointer">
                  Time range
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Mode-specific controls */}
          {config.mode === 'interval' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Interval (seconds)</Label>
                <span className="text-sm font-medium">{config.intervalSeconds}s</span>
              </div>
              <Slider
                value={[config.intervalSeconds]}
                onValueChange={([value]) => setConfig({ ...config, intervalSeconds: value })}
                min={1}
                max={Math.max(60, Math.floor(videoDuration / 2))}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                Extract a frame every {config.intervalSeconds} second{config.intervalSeconds !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {config.mode === 'count' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Number of frames</Label>
                <Input
                  type="number"
                  value={config.frameCount}
                  onChange={(e) => setConfig({ ...config, frameCount: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-24 h-8 text-right"
                  min={1}
                  max={100}
                />
              </div>
              <Slider
                value={[config.frameCount]}
                onValueChange={([value]) => setConfig({ ...config, frameCount: value })}
                min={2}
                max={Math.min(100, totalFrames)}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                {config.frameCount} frames evenly distributed across the video
              </p>
            </div>
          )}

          {config.mode === 'range' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start time</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={config.rangeStart}
                      onChange={(e) => setConfig({ ...config, rangeStart: Math.max(0, parseFloat(e.target.value) || 0) })}
                      className="h-8"
                      min={0}
                      max={videoDuration}
                      step={0.1}
                    />
                    <span className="text-xs text-muted-foreground">s</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>End time</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={config.rangeEnd}
                      onChange={(e) => setConfig({ ...config, rangeEnd: Math.min(videoDuration, parseFloat(e.target.value) || videoDuration) })}
                      className="h-8"
                      min={0}
                      max={videoDuration}
                      step={0.1}
                    />
                    <span className="text-xs text-muted-foreground">s</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Interval within range</Label>
                  <span className="text-sm font-medium">{config.rangeIntervalSeconds}s</span>
                </div>
                <Slider
                  value={[config.rangeIntervalSeconds]}
                  onValueChange={([value]) => setConfig({ ...config, rangeIntervalSeconds: value })}
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Frames to extract</span>
              <Badge variant={framesToExtract.length > 50 ? 'destructive' : 'secondary'}>
                {framesToExtract.length} frames
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Estimated size: ~{formatFileSize(estimatedSize)}
            </p>
            {framesToExtract.length > 50 && (
              <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Large extractions may take a while
              </p>
            )}
          </div>

          {/* Progress */}
          {isExtracting && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                Extracting frames... {progress}%
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExtracting}>
            Cancel
          </Button>
          <Button 
            onClick={handleExtract} 
            disabled={isExtracting || framesToExtract.length === 0}
          >
            {isExtracting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Camera className="mr-2 h-4 w-4" />
                Extract {framesToExtract.length} Frames
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
